/* A simulated microphone-sync diagram. It never accesses microphones or hardware. */
(() => {
  if (!window.customElements || customElements.get('mm-meeting-platforms')) return;

  const platforms = ['zoom', 'teams', 'meet', 'webex', 'obs', 'discord'];
  const sources = ['device', 'computer', ...platforms];
  const legDuration = 650;
  const autoplayInterval = 4000;
  const resumeDelay = 1000;

  customElements.define('mm-meeting-platforms', class extends HTMLElement {
    static get observedAttributes() { return ['data-motion']; }

    connectedCallback() {
      if (this.syncConnected) return;
      this.nodes = new Map([...this.querySelectorAll('[data-sync-node]')].map(node => [node.dataset.syncNode, node]));
      this.triggers = new Map([...this.querySelectorAll('[data-sync-trigger]')].map(button => [button.dataset.syncTrigger, button]));
      if (!sources.every(key => this.nodes.has(key) && this.triggers.has(key))) return;
      this.syncConnected = true;
      const connection = this.connection = (this.connection || 0) + 1;
      this.sequence = (this.sequence || 0) + 1;
      this.sequenceTimers = new Set();
      this.cleanups = [];
      this.idleTimer = null;
      this.nextAutoAt = null;
      this.busy = false;
      this.pendingAnnouncement = false;
      this.autoIndex = this.autoIndex || 0;
      this.targetMuted = typeof this.targetMuted === 'boolean' ? this.targetMuted : this.nodes.get('device').dataset.muted === 'true';
      this.signals = [...this.querySelectorAll('[data-sync-signal]')];
      this.announcement = this.querySelector('[data-sync-announcement]');
      this.network = this.querySelector('.mm-platforms__network');
      this.connections = this.querySelector('[data-sync-connections]');
      this.hovered = Boolean(this.network?.matches(':hover'));
      this.keyboardInput = true;
      this.focused = Boolean(this.network?.contains(document.activeElement));
      this.inView = false;
      this.observer = null;
      this.resizeObserver = null;
      this.motionQuery = null;

      const listen = (target, type, callback, options) => {
        target.addEventListener(type, callback, options);
        this.cleanups.push(() => target.removeEventListener(type, callback, options));
      };
      for (const [key, button] of this.triggers) {
        if (!sources.includes(key)) continue;
        button.disabled = false;
        listen(button, 'click', () => this.startSequence(key, true));
      }
      const syncFocus = target => {
        const wasFocused = this.focused;
        this.focused = Boolean(this.keyboardInput && target && this.network?.contains(target));
        if (this.focused) this.clearIdle();
        else if (wasFocused) this.scheduleIdle(true);
      };
      if (this.network) {
        listen(this.network, 'mouseenter', () => { this.hovered = true; this.clearIdle(); });
        listen(this.network, 'mouseleave', () => { this.hovered = false; this.scheduleIdle(true); });
        listen(this.network, 'focusin', event => syncFocus(event.target));
        listen(this.network, 'focusout', event => syncFocus(event.relatedTarget));
      }
      // Keep keyboard operation quiet without letting a mouse click latch autoplay off.
      listen(document, 'pointerdown', () => {
        this.keyboardInput = false;
        syncFocus(document.activeElement);
      }, true);
      listen(document, 'keydown', event => {
        if (event.altKey || event.ctrlKey || event.metaKey || ['Shift', 'Control', 'Alt', 'Meta'].includes(event.key)) return;
        this.keyboardInput = true;
        syncFocus(document.activeElement);
      });
      listen(document, 'visibilitychange', () => this.updateEnvironment());
      listen(window, 'pageshow', () => { this.updateGeometry(); this.updateEnvironment(); });
      listen(window, 'resize', () => { this.updateGeometry(); this.updateEnvironment(); }, { passive: true });
      listen(this, 'load', () => this.updateGeometry(), true);
      try {
        if (typeof window.matchMedia === 'function') this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      } catch (_) { /* An unknown motion preference uses the instant manual mode. */ }
      const onMotion = () => this.updateEnvironment();
      if (typeof this.motionQuery?.addEventListener === 'function') {
        listen(this.motionQuery, 'change', onMotion);
      } else if (typeof this.motionQuery?.addListener === 'function') {
        const media = this.motionQuery;
        media.addListener(onMotion);
        this.cleanups.push(() => media.removeListener(onMotion));
      }
      if (typeof window.IntersectionObserver === 'function') {
        try {
          this.observer = new IntersectionObserver(entries => {
            if (!this.syncConnected || connection !== this.connection) return;
            entries.forEach(entry => { if (entry.target === this) this.inView = entry.isIntersecting; });
            this.updateGeometry();
            this.updateEnvironment();
          }, { threshold: 0 });
          this.observer.observe(this);
        } catch (_) { this.observer?.disconnect(); this.observer = null; }
      }
      if (!this.observer) listen(window, 'scroll', () => this.updateEnvironment(), { passive: true });
      if (typeof window.ResizeObserver === 'function' && this.network) {
        try {
          this.resizeObserver = new ResizeObserver(() => {
            if (this.syncConnected && connection === this.connection) this.updateGeometry();
          });
          this.resizeObserver.observe(this.network);
          this.triggers.forEach(button => this.resizeObserver.observe(button));
        } catch (_) { this.resizeObserver?.disconnect(); this.resizeObserver = null; }
      }
      this.settle(false);
      this.dataset.ready = 'true';
      this.updateGeometry();
      this.updateEnvironment();
    }

    attributeChangedCallback() {
      if (this.syncConnected) this.updateEnvironment();
    }

    setNode(key, muted) {
      this.nodes.get(key).dataset.muted = String(muted);
      this.triggers.get(key).setAttribute('aria-pressed', String(muted));
    }

    clearIdle() {
      if (this.idleTimer !== null) window.clearTimeout(this.idleTimer);
      this.idleTimer = null;
      this.idleGeneration = (this.idleGeneration || 0) + 1;
    }

    canAnimate() {
      return this.syncConnected && this.isConnected && this.dataset.motion === 'true' &&
        Boolean(this.motionQuery) && !this.motionQuery.matches && !document.hidden && this.inView;
    }

    scheduleIdle(reset = false) {
      if (reset) {
        this.clearIdle();
        this.nextAutoAt = performance.now() + resumeDelay;
      }
      if (this.idleTimer !== null || !this.canAnimate() || this.hovered || this.focused || this.busy) return;
      if (this.nextAutoAt === null) this.nextAutoAt = performance.now() + resumeDelay;
      const connection = this.connection;
      const generation = this.idleGeneration;
      this.idleTimer = window.setTimeout(() => {
        if (connection !== this.connection || generation !== this.idleGeneration) return;
        this.idleTimer = null;
        if (!this.canAnimate() || this.hovered || this.focused || this.busy) return;
        const origin = sources[this.autoIndex % sources.length];
        this.autoIndex += 1;
        this.startSequence(origin, false);
      }, Math.max(0, this.nextAutoAt - performance.now()));
    }

    later(callback, delay) {
      const sequence = this.sequence, connection = this.connection;
      const timer = window.setTimeout(() => {
        this.sequenceTimers.delete(timer);
        if (this.syncConnected && this.isConnected && sequence === this.sequence && connection === this.connection) callback();
      }, delay);
      this.sequenceTimers.add(timer);
    }

    settle(announce) {
      this.sequence += 1;
      this.sequenceTimers.forEach(timer => window.clearTimeout(timer));
      this.sequenceTimers.clear();
      this.signals.forEach(signal => signal.removeAttribute('data-active'));
      for (const key of sources) {
        this.setNode(key, this.targetMuted);
        this.nodes.get(key).removeAttribute('data-origin');
      }
      this.busy = false;
      this.dataset.syncRunning = 'false';
      if (announce && this.pendingAnnouncement && this.announcement) {
        this.announcement.textContent = this.targetMuted
          ? 'Demo microphone muted across the device, computer and platforms.'
          : 'Demo microphone on across the device, computer and platforms.';
      }
      this.pendingAnnouncement = false;
    }

    activateSignals(kind, { only, except } = {}) {
      const matching = this.signals.filter(signal => signal.dataset.syncSignal === kind &&
        (!only || !signal.dataset.syncPlatform || signal.dataset.syncPlatform === only) &&
        (!except || signal.dataset.syncPlatform !== except));
      matching.forEach(signal => {
        signal.removeAttribute('data-active');
        signal.dataset.tone = this.targetMuted ? 'muted' : 'live';
      });
      // Commit the reset so rapid clicks can replay a finite CSS journey on the same path.
      if (matching.length) matching[0].getBoundingClientRect();
      matching.forEach(signal => signal.dataset.active = 'true');
      this.later(() => matching.forEach(signal => signal.removeAttribute('data-active')), legDuration);
    }

    startSequence(origin, manual) {
      if (!this.syncConnected || !sources.includes(origin)) return;
      this.clearIdle();
      this.settle(false);
      // Measure cadence from the start, so short and long routes both repeat every 4s.
      this.nextAutoAt = performance.now() + autoplayInterval;
      this.targetMuted = !this.targetMuted;
      this.pendingAnnouncement = manual;
      this.setNode(origin, this.targetMuted);
      if (!this.canAnimate()) {
        this.settle(true);
        return;
      }
      this.busy = true;
      this.dataset.syncRunning = 'true';
      this.nodes.get(origin).dataset.origin = 'true';
      const finish = () => { this.settle(true); this.scheduleIdle(); };
      if (origin === 'device') {
        this.activateSignals('device-out');
        this.later(() => { this.setNode('computer', this.targetMuted); this.activateSignals('platform-out'); }, legDuration);
        this.later(finish, legDuration * 2);
      } else if (origin === 'computer') {
        this.activateSignals('device-back');
        this.activateSignals('platform-out');
        this.later(finish, legDuration);
      } else {
        this.activateSignals('platform-back', { only: origin });
        this.later(() => {
          this.setNode('computer', this.targetMuted);
          this.activateSignals('device-back');
          this.activateSignals('platform-out', { except: origin });
        }, legDuration);
        this.later(finish, legDuration * 2);
      }
    }

    updateEnvironment() {
      if (!this.syncConnected) return;
      if (!this.observer) {
        const rect = this.getBoundingClientRect();
        this.inView = rect.width > 0 && rect.height > 0 && rect.bottom > 0 && rect.right > 0 &&
          rect.top < window.innerHeight && rect.left < window.innerWidth;
      }
      if (!this.canAnimate()) {
        this.clearIdle();
        this.nextAutoAt = null;
        this.settle(true);
      } else this.scheduleIdle();
    }

    updateGeometry() {
      if (!this.syncConnected || !this.network || !this.connections) return;
      const bounds = this.network.getBoundingClientRect();
      if (bounds.width <= 0 || bounds.height <= 0) return;
      this.connections.setAttribute('viewBox', `0 0 ${bounds.width} ${bounds.height}`);
      const rects = new Map(sources.map(key => [key, this.triggers.get(key).getBoundingClientRect()]));
      if ([...rects.values()].some(rect => rect.width <= 0 || rect.height <= 0)) return;
      const device = rects.get('device'), computer = rects.get('computer');
      const center = rect => [(rect.left + rect.right) / 2, (rect.top + rect.bottom) / 2];
      const d = center(device), c = center(computer);
      const horizontal = Math.abs(c[0] - d[0]) >= Math.abs(c[1] - d[1]);
      const point = value => `${Math.round((value[0] - bounds.left) * 10) / 10} ${Math.round((value[1] - bounds.top) * 10) / 10}`;
      const roundedPath = points => {
        const clean = points.filter((current, index) => !index || Math.hypot(current[0] - points[index - 1][0], current[1] - points[index - 1][1]) > .1);
        let path = `M${point(clean[0])}`;
        for (let index = 1; index < clean.length - 1; index += 1) {
          const previous = clean[index - 1], corner = clean[index], next = clean[index + 1];
          const before = Math.hypot(previous[0] - corner[0], previous[1] - corner[1]);
          const after = Math.hypot(next[0] - corner[0], next[1] - corner[1]);
          const radius = Math.min(8, before / 2, after / 2);
          const entry = corner.map((value, axis) => value + (previous[axis] - value) * radius / before);
          const exit = corner.map((value, axis) => value + (next[axis] - value) * radius / after);
          path += `L${point(entry)}Q${point(corner)} ${point(exit)}`;
        }
        return path + `L${point(clean[clean.length - 1])}`;
      };
      const gutter = Math.min(10, bounds.width * .025);
      const leftGutter = bounds.left + gutter, rightGutter = bounds.right - gutter;
      const platformLeft = Math.min(...platforms.map(key => rects.get(key).left));
      const platformBus = (computer.right + platformLeft) / 2;
      for (const key of ['device', ...platforms]) {
        let points;
        if (key === 'device' && horizontal) {
          const middle = (device.right + computer.left) / 2;
          points = [[device.right + 2, d[1]], [middle, d[1]], [middle, c[1]], [computer.left - 2, c[1]]];
        } else if (key === 'device') {
          const help = this.nodes.get('device').querySelector('.mm-platforms__configure')?.getBoundingClientRect();
          const gap = help && help.top > device.bottom ? Math.min(8, (help.top - device.bottom) / 2) : 8;
          const exitY = device.bottom + gap;
          points = [[d[0], device.bottom + 1], [d[0], exitY], [leftGutter, exitY], [leftGutter, c[1]], [computer.left - 2, c[1]]];
        } else {
          const target = rects.get(key), t = center(target);
          // Row gaps keep the path out of neighboring tiles and their setup links.
          const rowY = target.top - 6;
          const bus = horizontal ? platformBus : rightGutter;
          points = [[computer.right + 2, c[1]], [bus, c[1]], [bus, rowY], [t[0], rowY], [t[0], target.top - 2]];
        }
        const outward = roundedPath(points), backward = roundedPath([...points].reverse());
        this.connections.querySelector(`[data-sync-wire="${key}"]`)?.setAttribute('d', outward);
        const group = key === 'device' ? 'device' : 'platform';
        const suffix = key === 'device' ? '' : `[data-sync-platform="${key}"]`;
        this.connections.querySelector(`[data-sync-signal="${group}-out"]${suffix}`)?.setAttribute('d', outward);
        this.connections.querySelector(`[data-sync-signal="${group}-back"]${suffix}`)?.setAttribute('d', backward);
      }
    }

    disconnectedCallback() {
      if (!this.syncConnected) return;
      this.clearIdle();
      this.settle(false);
      this.syncConnected = false;
      this.connection += 1;
      this.observer?.disconnect();
      this.resizeObserver?.disconnect();
      this.cleanups.forEach(cleanup => cleanup());
      this.cleanups = [];
      this.triggers.forEach(button => button.disabled = true);
      delete this.dataset.ready;
    }
  });
})();
